/**
 * MobileNotificationTemplates.jsx
 * Verwaltung der automatischen App-Push-Benachrichtigungen (Templates + Toggles).
 * Ausgelagert aus MobileAppManagement, damit die Kunden-Kommunikation zentral im Marketing-Bereich liegt.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, ChevronUp, ChevronDown, Clock, Save, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileNotificationTemplates = () => {
  const token = localStorage.getItem('token');
  const [notifications, setNotifications] = useState([]);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/mobile-app/push-notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(res.data.notifications || []);
      } catch (error) {
        toast.error('Fehler beim Laden der Benachrichtigungen');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const toggleNotification = async (notificationType, enabled) => {
    try {
      const notif = notifications.find(n => n.notification_type === notificationType);
      const updatedNotif = { ...notif, enabled };
      await axios.put(
        `${API}/admin/mobile-app/push-notifications/${notificationType}`,
        updatedNotif,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev =>
        prev.map(n => (n.notification_type === notificationType ? updatedNotif : n))
      );
      toast.success(enabled ? 'Benachrichtigung aktiviert' : 'Benachrichtigung deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const saveNotificationSettings = async (notificationType) => {
    try {
      const notif = notifications.find(n => n.notification_type === notificationType);
      await axios.put(
        `${API}/admin/mobile-app/push-notifications/${notificationType}`,
        notif,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const sendTestNotification = async (notificationType) => {
    try {
      await axios.post(
        `${API}/admin/mobile-app/push-notifications/test`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { notification_type: notificationType },
        }
      );
      toast.success('Test-Benachrichtigung gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden');
    }
  };

  return (
    <div className="space-y-4" data-testid="mobile-notification-templates">
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          Push-Benachrichtigungen (App-Templates)
        </h3>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <Bell className="w-4 h-4 inline mr-2" />
          Konfiguriere Push-Benachrichtigungen für die Mobile App. Benutzer können diese individuell in der App aktivieren/deaktivieren.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 p-2">Lädt…</p>
      ) : (
        <div className="space-y-3">
          {notifications.filter(n => !['nutrition_reminder_gate', 'nutrition_reactivation'].includes(n.notification_type)).map(notif => (
            <div
              key={notif.notification_type}
              className="bg-gray-100 rounded-xl border border-gray-200 overflow-hidden"
            >
              <div
                className="p-4 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedNotification(
                  expandedNotification === notif.notification_type
                    ? null
                    : notif.notification_type
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${notif.enabled ? 'bg-yellow-500/20' : 'bg-gray-200'}`}>
                    <Bell className={`w-5 h-5 ${notif.enabled ? 'text-yellow-500' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{notif.title_template}</p>
                    <p className="text-sm text-gray-500">{notif.body_template}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleNotification(notif.notification_type, !notif.enabled);
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notif.enabled ? 'bg-yellow-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      notif.enabled ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                  {expandedNotification === notif.notification_type ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {expandedNotification === notif.notification_type && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
                  {notif.notification_type === 'inactivity_reminder' ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <label className="text-xs text-gray-500 block mb-2">
                            Erste Erinnerung nach
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="90"
                              value={notif.inactivity_days_threshold || 14}
                              onChange={(e) => {
                                const updated = { ...notif, inactivity_days_threshold: parseInt(e.target.value) || 14 };
                                setNotifications(prev => prev.map(n =>
                                  n.notification_type === notif.notification_type ? updated : n
                                ));
                              }}
                              className="w-20 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-yellow-500 focus:outline-none"
                            />
                            <span className="text-gray-500 text-sm">Tagen Inaktivität</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3">
                          <label className="text-xs text-gray-500 block mb-2">
                            Folge-Erinnerung nach
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={notif.repeat_after_days || 7}
                              onChange={(e) => {
                                const updated = { ...notif, repeat_after_days: parseInt(e.target.value) || 7 };
                                setNotifications(prev => prev.map(n =>
                                  n.notification_type === notif.notification_type ? updated : n
                                ));
                              }}
                              className="w-20 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-yellow-500 focus:outline-none"
                            />
                            <span className="text-gray-500 text-sm">Tagen</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3">
                          <label className="text-xs text-gray-500 block mb-2">
                            Max. Erinnerungen
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={notif.max_reminders || 3}
                              onChange={(e) => {
                                const updated = { ...notif, max_reminders: parseInt(e.target.value) || 3 };
                                setNotifications(prev => prev.map(n =>
                                  n.notification_type === notif.notification_type ? updated : n
                                ));
                              }}
                              className="w-20 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm focus:border-yellow-500 focus:outline-none"
                            />
                            <span className="text-gray-500 text-sm">mal</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-700">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Beispiel: Nach {notif.inactivity_days_threshold || 14} Tagen ohne Aktivität wird die erste Erinnerung gesendet,
                          dann nach {notif.repeat_after_days || 7} weiteren Tagen eine Folge-Erinnerung (max. {notif.max_reminders || 3}x).
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => saveNotificationSettings(notif.notification_type)}
                          className="px-4 py-2 bg-yellow-500 rounded-lg text-sm text-black font-medium hover:bg-yellow-400 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          Speichern
                        </button>
                        <button
                          onClick={() => sendTestNotification(notif.notification_type)}
                          className="px-4 py-2 bg-gray-200 rounded-lg text-sm text-gray-900 hover:bg-gray-300 flex items-center gap-2"
                        >
                          <Send className="w-4 h-4" />
                          Test senden
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {notif.default_time && (
                          <div>
                            <p className="text-gray-500">Standard-Uhrzeit</p>
                            <p className="text-gray-900 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {notif.default_time} Uhr
                            </p>
                          </div>
                        )}
                        {notif.interval_minutes && (
                          <div>
                            <p className="text-gray-500">Intervall</p>
                            <p className="text-gray-900">Alle {notif.interval_minutes} Minuten</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-500">Aktive Tage</p>
                          <p className="text-gray-900">
                            {notif.days_active?.length === 7
                              ? 'Jeden Tag'
                              : notif.days_active?.length === 0
                                ? 'Ereignisbasiert'
                                : notif.days_active?.join(', ') || 'Jeden Tag'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => sendTestNotification(notif.notification_type)}
                        className="mt-4 px-4 py-2 bg-gray-200 rounded-lg text-sm text-gray-900 hover:bg-gray-300 flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Test senden
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileNotificationTemplates;
